import React, { ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DownTrianglePointer } from '@/components/Icons';

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  /** Extra vertical nudge on top of the default flush-above-trigger position. */
  translateY?: string;
  animate?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({ content, children, translateY, animate }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>();
  const triggerRef = useRef<HTMLDivElement>(null);

  // Rendered via a portal and positioned from the trigger's measured rect (rather than
  // an absolute child of a relative wrapper) so the tooltip escapes any clipping/scroll
  // ancestor - e.g. DataTable's overflow-x-auto container - instead of being cut off.
  const showAt = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({ top: rect.top, left: rect.left + rect.width / 2 });
      setShowTooltip(true);
    }
  };
  const hide = () => setShowTooltip(false);

  return (
    <div
      ref={triggerRef}
      className="inline-flex cursor-pointer items-center justify-center"
      onClick={() => (showTooltip ? hide() : showAt())}
      onMouseEnter={showAt}
      onMouseLeave={hide}>
      {children}
      {showTooltip &&
        position &&
        createPortal(
          <div
            className={`fixed bg-dark-navy-30 w-max max-w-48 text-white text-center font-medium rounded py-2 px-3 text-[10px] shadow-[0px_1px_2px_rgba(0,0,0,0.08)] pointer-events-none ${animate ? 'transition-all duration-300' : ''} z-99`}
            style={{
              top: position.top,
              left: position.left,
              transform: `translate(-50%, calc(-100% + ${translateY ?? '0px'}))`
            }}>
            {content}
            {/* translate-y-full (rather than top-full) accounts for DownTrianglePointer's icon
                box being taller than the triangle it renders, which otherwise leaves a gap. */}
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 translate-y-full">
              <DownTrianglePointer size="sm" variant="inherit" />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Tooltip;
